'use client';
import SplitFlap from './SplitFlap';
import AvatarFlap from './AvatarFlap';
import StatusFlap from './StatusFlap';

/* ============================================================
   MatchupRow — one departures-board line for a single matchup.

   Layout (§2):
     [avatar][rank][team name][points]   [avatar][rank][team name][points] | [status]

   In the PLAYOFFS one narrow slot is added at the left:
     [medal][avatar][rank][name][pts] vs [avatar][rank][name][pts] | [status]

   - medal  : one emoji, or blank. Gold for the title game, bronze for the
              third-place game, nothing for anything else.

              THIS REPLACED A 9-CELL SPLITFLAP LABEL. On real boards that
              label pushed the status flip-dot off the right edge -- it
              clipped on 7 of 8 tested viewports including 1080p. The round
              NAME moved to the header (Q-FINAL / SEMFNAL / 'SHIP), where
              there was always room, and the row keeps only the mark that
              distinguishes games WITHIN a round.

              Emoji are NOT in SplitFlap's CHARSET and would riffle to
              blanks, so the medal is a plain span with an explicit emoji
              font stack -- never a flap.
   - avatar : split-flap-clipped image (AvatarFlap). Slot-machines only on
     a week change -- `spin={week}` is threaded straight through.
   - rank   : 2 split-flap glyphs, right-aligned. In the playoffs this is
     the team's SEED, which is the same number -- regular-season rank IS
     the seed -- so no code changes, only meaning.
   - name   : team name, split-flap, truncated to `nameWidth` (default 14).
   - points : split-flap in amber; "--" during pregame (no score yet).
   - status : one shared flip-dot cell for the matchup (PROJECTD/ACTIVE/FINAL).

   Pure presentation over the adapter's render-ready row. Only the cells
   whose values changed will actually flip (each SplitFlap/AvatarFlap diffs
   its own target internally), so re-rendering the board is cheap and only
   the things that moved animate.
   ============================================================ */

const AMBER = '#ffb300';
const DIM = '#5c6068';

// MEDALS, not flaps.
//
// The round label used to be a 9-cell SplitFlap at the head of every
// playoff row. On real boards that pushed the status flip-dot off the
// right edge -- the row simply ran out of horizontal room. A medal is one
// glyph where nine cells used to be, and it reads faster anyway.
//
// IMPORTANT: these are plain spans, NOT SplitFlaps. Emoji are not in
// SplitFlap's CHARSET (" ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:./-'&·"), so
// a medal inside a flap would riffle to a blank. Verified.
//
// The header now carries the round name (Q-FINAL / SEMFNAL / 'SHIP), so
// the row only needs to mark which game THIS is -- and only for the games
// where that's meaningful.
const MEDAL = {
  CHAMPIONSHIP: '\u{1F947}',   // gold
  '3RD PLACE': '\u{1F949}',    // bronze
};

// Everything else -- semifinals, quarterfinals, 5th place, byes -- gets no
// medal. A medal on every row would make the medals meaningless; the point
// is that the title game and the bronze game stand out from their
// neighbours in the same round.
export function medalFor(label) {
  return MEDAL[label] || '';
}

function Team({ team, week, pool, size, nameWidth, side, dim = false, boardStatus }) {
  if (!team) {
    return (
      <span className="mr-team mr-bye" style={{ opacity: 0.4 }}>
        <SplitFlap text="BYE" size={size} width={nameWidth} align="center" />
      </span>
    );
  }
  const projected = boardStatus === 'project' || team.points == null;
  const pts = projected ? '--' : String(Math.round(team.points));
  return (
    <span
      className={`mr-team mr-${side}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        // An eliminated team lingering on the board (only when
        // dropConsolation is off) reads as a ghost, not a contender.
        opacity: dim ? 0.45 : 1,
      }}
    >
      <AvatarFlap src={team.avatar || ''} pool={pool} spin={week} size={size} alt={team.name} />
      <SplitFlap text={String(team.rank ?? '')} size={size} width={2} align="right" />
      <SplitFlap text={team.name} size={size} width={nameWidth} align="left" />
      <SplitFlap text={pts} size={size} width={3} align="right" color={AMBER} />
    </span>
  );
}

export default function MatchupRow({
  row,               // { teamA, teamB, boardStatus, playoffLabel?, isBye? }
  week,              // current week; threaded to avatars for spin-on-rollover
  pool = [],         // league avatar URLs for the slot-machine
  size = 32,
  nameWidth = 14,
  revealDelay = 0,   // stagger the status wipe when rows cascade
  showRoundLabel = false, // true during playoff weeks
}) {
  // The round NAME now lives in the header. The row only marks which game
  // this is, and only for the two that carry their own weight.
  const medal = showRoundLabel ? medalFor(row.playoffLabel) : '';
  const isTitle = row.playoffLabel === 'CHAMPIONSHIP';
  const isBye = Boolean(row.isBye);
  const consolation = row.isPlayoff === false;

  return (
    <div
      className={`mr-row${isTitle ? ' mr-title' : ''}${isBye ? ' mr-byerow' : ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}
    >
      {/* One fixed-width slot during the playoffs, whether or not THIS row
          has a medal -- otherwise medalled and unmedalled rows would start
          at different x-positions and the board would look ragged. Costs
          about one character of width instead of the nine cells the old
          label flap took, which is what was pushing the status dot off the
          right edge. */}
      {showRoundLabel ? (
        <span
          className="mr-medal"
          aria-hidden={medal ? undefined : true}
          style={{
            display: 'inline-block',
            width: size,
            flex: `0 0 ${size}px`,
            textAlign: 'center',
            fontSize: Math.round(size * 0.82),
            lineHeight: 1,
            // Emoji render from the system font, never the flap face.
            fontFamily:
              '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif',
          }}
        >
          {medal}
        </span>
      ) : null}

      <Team
        team={row.teamA} week={week} pool={pool} size={size}
        nameWidth={nameWidth} side="a" dim={consolation}
        boardStatus={row.boardStatus}
      />

      {/* A bye has no opponent, so it has no "vs". Rendering one would
          make the empty half look like a data failure instead of a rest. */}
      {isBye ? (
        <span
          className="mr-bye-note"
          style={{
            color: DIM,
            fontSize: size * 0.55,
            fontWeight: 700,
            letterSpacing: '.18em',
          }}
        >
          — BYE —
        </span>
      ) : (
        <>
          <span
            className="mr-vs"
            style={{
              color: '#3a3d44',
              fontSize: size * 0.55,
              fontWeight: 700,
              letterSpacing: '.05em',
            }}
          >
            vs
          </span>
          <Team
            team={row.teamB} week={week} pool={pool} size={size}
            nameWidth={nameWidth} side="b" dim={consolation}
            boardStatus={row.boardStatus}
          />
        </>
      )}

      <span className="mr-status" style={{ marginLeft: 8 }}>
        {/* A bye has no score and therefore no status; a FINAL flap next to
            an empty half is noise. */}
        {isBye ? null : (
          <StatusFlap
            status={row.boardStatus}
            matchSize={size}
            speed={1}
            revealDelay={revealDelay}
          />
        )}
      </span>
    </div>
  );
}
